import { AM_CATEGORIES } from "./ConstAccessMethodsMock"

//General remedies that tend to apply to any situation:
const NATURAL_CHANGE = {
  label: 'Consider whether things might be expected to improve, perhaps after a learning period with a new access method, or after additional recovery from a medical condition. ',
  help: ``
}
const BASIC_HEADER = {
  label: 'Try one of the Basic 3 remedies:',
  help: ''
}
const BASICREM1 = {
  label: 'Adjust the setup of the current access method',
  help: `
    <div>You might try keeping the same type of access method, but adjusting it to be more tailored to the user.</div>
    <div>Adjustments to consider include:</div>
    <ul>
      <li>Position. Bring device closer to the user. This needs to be balanced - it can be so close that they have neck/eye strain from looking at it.</li>
      <li>Settings. Review any settings in the access method to see if any of them might benefit the user.</li>
      <li>Body Site. Support the current body site for better function. Change how the body site is being used (like using a typing cuff instead of direct keypress). Or try a different body site.</li>
    </ul> 
    <div>In the low/no-tech case, if there is a partner involved who is trying to observe the user's signal, consider whether the partner's participation could be 'adjusted', perhaps by changing position or increasing attentiveness.</div> 
    `,
    className: 'aa-kludge-indent'
}
const BASICREM2 = {
  label: 'Try a different type of access method',
  help: `
    <div>Consider trying a different access method if:</div>
    <ul>
      <li>Performance with the current method is poor and seems unlikely to improve.</li>
      <li>Motivation with the current method is low.</li>
      <li>There's at least one obvious alternative that seems to have potential. (Example: client is struggling with hand-typing, but has good head control.)</li>
      <li>Client is interested in exploring other options.</li>
    </ul>
  `,
  className: 'aa-kludge-indent'
}
const SEATING_HELP = `
  <div>The goal is to be able to maintain an active, functional, and comfortable posture.</div>
  <div>Some ideas to consider, where applicable:</div>
  <ul>
    <li>Bring target closer to the user (switch, keyboard, communication board, etc.). This needs to be balanced - it can be so close that they have neck/eye strain from looking at it.</li>
    <li>Does the user rely on upper extremity support to be accurate with reaching? Work with your seating and positioning specialist, ATP, and/or vendor to explore ways to increase trunk stability in order to free up arms for functional tasks.</li>
    <li>Try using a pillow or towel roll to help stabilize an upper extremity when using small hand movements or to offload the hand to ease hand movements.</li>
    <li>Consider a lap tray to support the upper extremities.</li>
    <li>Is the user a candidate for mobile arm supports? Reach out to OT/PT or your seating vendor for help on this.</li>
    <li>Try adjusting the wheelchair tilt position (when available and tolerated).  Sometimes a slight change in gravitational pull can change how successful, or not successful a movement/activation attempt will be.</li>
    </ul>
  <div>Links for more info:</div>
  <ul>
    <li><a href="https://www.myshepherdconnection.org/sci/wheelchair-positioning" target="_blank" rel="noopener">Wheelchair positioning, Shepherd Center</a></li> 
  </ul>
`;
const JUMP_SHIP_HELP = `
  <p>It's not always easy to know when to stick with the current method and keep trying to improve vs when to pivot to a new access method.
   Here are some considerations that may help you think through this decision:</p>
  <ul>
    <li>Does the client need or want some type of functioning access right now?  If there's an urgent need, try the "simplest thing that could possibly work" approach,
    and get going with something that provides some initial success.</li>
    <li>With the current method, is performance improving steadily? Or has it plateaued at a less-than-fully-successful spot?</li>
    <li>How much time and effort (on the part of the client/family/caregiver) can be dedicated to practice and learning?</li>
    <li>Is there a mismatch between performance and desire to use a method -- i.e., they want to use a particular access method
    but are not doing well with it, or vice versa? In this case more objective data on a range of access methods might be beneficial.
    <li>What are the other potential access methods that this client might be able to use?</li>
    <li>Do you anticipate that the client can successfully learn / use the new access method (think about the variety of contexts and environmental barriers that may play a role)?</li>
    <li>Does the client seem genuinely motivated to try a new access method?</li>
    <li>How long do you anticipate the client will need to use the access method?</li>
    <li>Will the access method be impacted by health decline, degenerative conditions, or other changes?</li>
    <li>Consider whether the method requires optimal set-up and conditions to be used successfully -- is that sustainable?</li>
    <li>Does the client tolerate shifting focus easily? If not, be careful about bouncing back and forth between access methods.</li>
  </ul>
  <p>Keep in mind that this is not necessarily an either / or question. It might make sense to get something for now that works 
  (a 'quick and dirty' solution), while also working toward a method that might be better long term but will require more practice.</p>
  <p>With respect to seating / positioning, do what you can right now to facilitate an active, functional, and comfortable posture. 
    Reach out to OT / PT or your seating vendor for help as well.</p>
`
const BASICREM3 = {
  label: 'Adjust the seating / positioning system',
  help: SEATING_HELP,
  className: 'aa-kludge-indent'
}
const BASICREM4 = {
  linkText: 'Learn more about which basic remedy to try',
  help: JUMP_SHIP_HELP,
  className: 'aa-kludge-indent'
}
export const REMEDIES_OVERALL = [ NATURAL_CHANGE, BASIC_HEADER, BASICREM1, BASICREM3, BASICREM2, BASICREM4  ];

//Specific remedies/considerations that are more unique to particular situations
//Any remedy can have a .help property that explains more about the remedy. .help needs to be in HTML (see examples)
const REGULAR_FATIGUE1 = {
  label: 'Set expectations that building up a tolerance for using the access method may be necessary. Give permission and encourage time for breaks.'+
    ' Have a backup plan in place that the user can switch to when they need a break.'
}
const REGULAR_FATIGUE2 = {
  label: 'Consider basic ergonomics: neutral position for viewing the screen or communication board, input device in an easily accessible position, and comfortable body posture'
}
const REGULAR_FATIGUE3 = {
  label: "If you are working on low/no-tech communication, and the user is experiencing significant fatigue, consider starting with a binary signaling system (like yes / no to make choices). More complex methods like partner-assisted scanning require several minutes of physical and mental effort. You may need to wait to progress to this method until the individual has sufficient cognitive / attentional abilities and stamina."
}

const REDFLAG_FATIGUE = {
  label:'Excessive fatigue could be a red flag. A fatiguing access method is usually one that will not be used very often.'+
    ' And if the user has a degenerative condition such as ALS, fatigue while using the access method is a particular red flag and might be a contraindication.'
}
const REDFLAG_PAIN = {
  label: 'Pain could be a red flag. Ideally there should not be pain when using any access method.'+
   ' If the user has chronic pain or a repetitive stress condition, using the access method should not make the pain worse.'
}
const PAIN_PATTERN2 = {
  label: 'Does the pain calm down once the user stops using the access method? '
}
const PAIN_PATTERN1 = {
  label: 'Is there a pattern to the pain? Does it occur only when they are using the access method? Or does the access method make existing pain worse?'
}
const PAIN_PATTERN3 = {
  label: 'Consider pulling in other professionals such as a physiatrist, OT, or PT to problem-solve.'
}
const SEATING_GENERAL = {
  label: 'Consider adjusting the seating / positioning system',
  help: SEATING_HELP
}
const SEATING_REFERRAL = {
  label: 'Refer to interdisciplinary team, ATP, seating/positioning specialist for help if you suspect the user is struggling due to instability or other seating issues.'
}
const UNDERSTANDING_GENERAL1 = {
  label: 'Consider how many layers there are to the activity itself. Is the activity you are asking the user to do too complicated? Are there ways to simplify it?'
}
const UNDERSTANDING_GENERAL2 = {
  label: "Consider the complexity of using the access method to perform the activity.  Are there ways to better match the task requirements to the user’s abilities?"
}
const UNDERSTANDING_GENERAL3 = {
  label: "Especially with a new access method, it may help to give the user time to explore the access method itself before using it to do functional tasks."
}
const ATTENTION_ENV = {
  label: "Can you reduce distractions in the environment?"
}
const ATTENTION_ACTIVITY = {
  label: "Is the activity itself a source of distraction? If you are setting up your own activities, try to choose things that are motivating and interesting, but not too distracting for the user.",
}
const ATTENTION_SPLIT = {
  label: "Does the user need to split their attention between doing the activity and using the access method?",
  help: `
  <div>Sometimes there can be opposing requirements between an activity and the access method, and this can present extra demands on attention. 
  <br/>For example, an activity may be in front of you and to the right, but to use the access method 
  you need to move away from the activity and to the left. Try aligning the activity and the access method where possible.
  </div>
  `
}
const ATTENTION_NOTECH = {
  label: "If you are working on low/no-tech communication, and attention is challenging for the user, consider starting with a binary signaling system (like yes / no to make choices). More complex methods like partner-assisted scanning require attention and physical effort for several minutes. You may need to wait to progress to this method until the individual has sufficient cognitive / attentional abilities and stamina."
}

const MOTIVATION_ACTIVITY = {
  label: 'Try to choose activities that are motivating, age-appropriate, and interesting to the user.  Or at the very least, choose something that relates clearly to a larger goal that the user has.'
}
const MOTIVATION_FEEDBACK = {
  label: "Is the user getting enough feedback from the activity and the access method to know how they’re doing? Can they see / hear / feel the feedback sufficiently? (With partner-assisted scanning, is the partner giving clear feedback to the user?)"
}
const VISION_REVIEW = {
  label: 'Review what you found in the visual abilities section for clues about why vision might be creating a challenge.',
}
const VISION_LOS = {
  label: "Also consider that some users need to be able to see the input device and the activity at the same time. If that’s the case you need to make sure everything is in the line of sight."
}

//Keyboard remedies:
const KEY_ISOLATE = {
  label: 'To help isolate key presses, consider: '
}
const KEY_ISOLATE1 = {
  label: 'Repositioning the keyboard, perhaps to a more vertical plane',
  className: 'aa-kludge-indent'
}
const KEY_ISOLATE2 = { 
  label: 'Stylus or a control accessory like a typing cuff',
  className: 'aa-kludge-indent'
}
const KEY_ISOLATE3 = {
  label: 'Keyguard',
  className: 'aa-kludge-indent',
  help: `
    <div>A keyguard sits on top of the keyboard and creates a physical guide around each key.</div>
    <div>Search the web for 'keyguard', or see <a target="_blank" href="https://bltt.org/keyguards/">this resource</a> for more details.</div>
  `
}
const KEY_ISOLATE4 = {
  label: 'A large-key keyboard',
  className: 'aa-kludge-indent'
}
const KEY_REPEAT = {
  label: 'Use the keyboard accessibility settings to adjust the key repeat speed and add a key repeat delay.',
  help: `<p>Keyboard accessibility settings are features built-in to Windows, Apple, and other devices. If you haven't used them before, a quick internet search should get you going (example: 'change key repeat delay Apple').</p>
    <p>On a Windows computer, try the <a target="_blank" href="https://kpronline.com/kbwiz">free Keyboard Wizard program</a> to determine what keyboard accessibility settings are right for you. It sets them for you, as well!</p>
  `
}
const KEY_RANGE2 = {
  label: 'Try a mini / compact keyboard. Add a stylus if needed.'
}
const KEY_RANGE1 = {
  label: 'Changing the position of the keyboard and/or the user may help.'
}
const KEY_MODIFIERS = {
  label: 'Turn on the Sticky Keys feature, in the keyboard accessibility settings built-in to your device.',
  help: `<p>Keyboard accessibility settings are features built-in to Windows, Apple, and other devices. If you haven't used Sticky Keys before, a quick internet search should get you going (example: 'Sticky Keys Windows').</p>
    <p>On a Windows computer, try the <a target="_blank" href="https://kpronline.com/kbwiz">free Keyboard Wizard program</a> to determine what keyboard accessibility settings are right for you. It sets them for you, as well!</p>
  `
}
const SEE_KEYS1 = {
  label: `Try a keyboard with high-contrast keys. Example internet search: 'keyboard yellow keys' or 'low vision keys'`
}

//Pointing device remedies:
const MOVE_POINTER = {
  label: 'Try to get more specific about what sort of trouble the user is having when moving the mouse pointer.  Revisit the issue checkboxes for ideas, and check any that apply.',
  help: `<p>For head tracking, this short video may also give you some ideas:</p>
  <div style="padding:70% 0 0 0;position:relative;"><iframe src="https://player.vimeo.com/video/850631929?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen style="position:absolute;top:0;left:0;width:100%;height:100%;" title="head-tracking-tips-and-tricks"></iframe></div><script src="https://player.vimeo.com/api/player.js"></script>
  `
}
const MAKE_SELECTION = {
  label: `Try to get more specific (if you haven't already): is it trouble with clicking the button? clicking without intending to? keeping the pointer still while clicking?  Revisit the issue checkboxes for ideas, and check any that apply.`
}
const MAKE_SELECTION1 = {
  label: `Keep in mind that there are a few ways to click, including physical button on the pointing device, dwell, and external switch. One of these may work better for your client.`
}
const POINTER_RANGE1 = {
  label: 'Increase mouse sensitivity via accessibility settings',
  help: `<p>Accessibility settings are features built-in to Windows, Apple, and other devices. If you haven't used them before, a quick internet search should get you going (example: 'Mouse accessibility settings').</p>
  <p>On a Windows computer, try the <a target="_blank" href="https://kpronline.com/ptwiz">free Pointing Wizard program</a> to determine what pointing accessibility settings are right for you. It sets them for you, as well!</p>
  `
}
const POINTER_RANGE2 = {
  label: 'Try a different style / brand of pointing device, as some have different scaling depending on the style'
}
const POINTER_RANGE3 = {
  label: 'When using head controls: Cue user to over-reach past the side of the screen that they CAN reach. This helps reset the mouse pointer to then reach farther in the opposite direction.',
  help: `<p>This short video may also give you some ideas:</p>
  <div style="padding:70% 0 0 0;position:relative;"><iframe src="https://player.vimeo.com/video/850631929?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen style="position:absolute;top:0;left:0;width:100%;height:100%;" title="head-tracking-tips-and-tricks"></iframe></div><script src="https://player.vimeo.com/api/player.js"></script>
  `
}
const OVERSHOOT1 = {
  label: 'The snap-to accessibility feature can be useful for dialogue boxes, but you have to choose if you default to "ok" vs "cancel"'
}
const OVERSHOOT2 = {
  label: 'Adjust mouse sensitivity via accessibility settings (usually lower in this case)'
}
const OVERSHOOT3 = {
  label: 'Increase size of target items when possible, through accessibility settings'
}
const OVERSHOOT4 = {
  label: 'Use a different access method, such as voice, for access to smaller targets that are too difficult to land on'
}


const CLICK_MOVE1 = {
  label: 'Consider a device with more separation between the select buttons and the pointer movement (like a trackpad, trackball, or joystick)'
}
const CLICK_MOVE2 = {
  label: "If that doesn't help, consider using a separate switch to do the clicking"
}
const CLICK_MOVE3 = {
  label: "Assess access to the switch for clicking. Can the user be positioned in such a way that they don't move when activating and releasing the switch?"+
    " If not, explore different switch sites. If no other switch sites are available, consider dwell clicking or voice-activated clicking with speech recognition software (built-in or third party)."
}
const CLICK_MOVE4 = {
  label: 'Would user be much more accurate or less fatigued using the right-click button for the primary click function? If so, activate this in the accessibility settings.'
}
const CLICK_MOVE5 = {
  linkText: 'See a video with more on these options',
  help: `<p>This short video may help you with ideas for making it easier for the user to click successfully:</p>
  <div style="padding:70% 0 0 0;position:relative;"><iframe src="https://player.vimeo.com/video/917630073?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" style="position:absolute;top:0;left:0;width:100%;height:100%;" title="Issues with accurate mouse clicks"></iframe></div><script src="https://player.vimeo.com/api/player.js"></script>
  `
}
const LIFT_MOVE1 = {
  label: "If using a mouse, consider a pointing device that is more stationary, like a trackpad or trackball."
}
const LIFT_MOVE2 = {
  label: "With a trackball, try limiting contact with the trackball to one single point (like a single knuckle, side of the hand, stylus, etc). Keep the fingers from dragging across the ball."
}
const LIFT_MOVE3 = {
  label: "With a trackpad, try having a single point of contact with the trackpad (like a single knuckle, stylus, etc.). It may also help to change position of the trackpad to be closer and angled more vertically."
}
const LIFT_MOVE4 = {
  label: "With a joystick, consider a different type of handle -- a goalpost, T-handle, long and vertical, ball-shaped, etc."
}
const DOUBLE_CLICK1 = {
  label: 'Try adjusting the double-click speed in accessibility settings'
}
const DOUBLE_CLICK2 = {
  label: 'Reduce the need to double-click (in your operating system settings)'
}
const DOUBLE_CLICK3 = {
  label: "If that doesn't help, consider mouse emulation software (such as Dragger, Point-N-Click)"
}
const RIGHT_CLICK1 = {
  label: 'Consider a device with a different layout of the buttons, such as a trackball'
}
const RIGHT_CLICK2 = {
  label: 'Consider a separate switch for right-clicking'
}
const RIGHT_CLICK2_1 = {
  label: 'Can use a switch interface to achieve a right click (such as Swifty)',
  className: 'aa-kludge-indent'
}
const RIGHT_CLICK2_2 = {
  label: 'Sip and puff would provide 2 switches (click and right-click) from a single access point',
  className: 'aa-kludge-indent'
}
const RIGHT_CLICK3 = {
  label: "Consider mouse emulation software (such as Dragger, Point-N-Click)"  
}
const RIGHT_CLICK4 = {
  label: "You might try voice-activated clicking with speech recognition software (built-in or third party)"
}
const LOCATE_POINTER1 = {
  label: "Adjust pointer to be larger, higher contrast, or visually more interesting"
}
const LOCATE_POINTER2 = {
  label: "Add a trail to the pointer to increase visibility when the pointer moves (in settings)"
}
const LOCATE_POINTER3 = {
  label: "Set the option to use Ctrl key to show location of pointer (in settings)"
}
const TRACK_POINTER1 = {
  label: 'You can try enlarging the pointer, and/or adding a trail to the pointer (in settings)'
}
const TRACK_POINTER2 = {
  label: "When using head controls: some users have trouble dissociating eye movements from head movement. Some may need to initiate head movements by first moving their eyes in that direction. If practice doesn't improve things, you may need to explore other access options."
}
//Switch remedies
const SW_POSITION = {
  label: "Adjust the switch position",
  help: `<p>This short video may give you some ideas:</p>
  <div style="padding:70% 0 0 0;position:relative;"><iframe src="https://player.vimeo.com/video/807552878?h=5af0fa7509&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen style="position:absolute;top:0;left:0;width:100%;height:100%;" title="1-sw-position.mp4"></iframe></div><script src="https://player.vimeo.com/api/player.js"></script>
  `
}
const SW_POSITION1 = {
  label: "Try supporting the extremity and position the switch vertically",
  className: "aa-kludge-indent"
}
const SW_POSITION2 = {
  label: "For trouble with release, turn the switch upside down so gravity can help",
  className: "aa-kludge-indent"
}
const SW_POSITION3 = {
  label: "Remind the user to relax into a resting position before placing a switch",
  className: "aa-kludge-indent"
}
const SW_POSITION4 = {
  label: "With head movement, observe the direction of the head movement (true lateral flexion? anteriolateral flexion? or is it more rotation?"+
    " We are trying to capture this movement, so position the switch accordingly. Try to make contact with bony prominences.",
  className: "aa-kludge-indent"
}
const SW_STABILITY = {
  label: "Stabilize the switch",
  help: `<p>This short video may give you some ideas:</p>
  <div style="padding:70% 0 0 0;position:relative;"><iframe src="https://player.vimeo.com/video/807552966?h=41e9b745cc&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen style="position:absolute;top:0;left:0;width:100%;height:100%;" title="2-sw-stability.mp4"></iframe></div><script src="https://player.vimeo.com/api/player.js"></script>
  `
}
const SW_STABILITY1 = {
  label: "Especially if the switch is placed on a soft surface, it may squish further into the soft surface instead of activating.",
  className: "aa-kludge-indent"
}
const SW_STABILITY2 = {
  label:     " Same goes for squishy body areas versus bony prominences.",
  className: "aa-kludge-indent"
}
const SW_ISOLATED = {
  label: "Try a more isolated movement and support the extremity. This can help the user not have to look at their body part to hit the switch."+
    " It can provide more proprioceptive input to help the user better understand where they are in space."
}
const SW_ACTIVITY= {
  label: "Can the user generate the movement to activate the switch only when looking at their extremity and the switch? If so, keep the activity very, very simple in the hopes of not distracting the user while attending to the activity and also generating a movement."
}
const SW_VISION = {
  label: "Does the user need to be able to see the switch and the activity at the same time? If so, make sure the switch and activity are both in line of sight. (If you're not sure, watch the user's eyes: do they bounce to the switch when they intend to move, then back to the screen after movement?)"
}
const SW_CONSISTENT = {
  label: 'A mismatch between movement and attention can be difficult. Like when an activity is in front of you and to the right, but you need to move away from the activity and to the left to activate the switch.'+
    ' Try positioning the switch so the user is engaged in the activity and moving toward the activity when activating the switch.'
}
const SW_FEEDBACK = {
  label: 'Make sure the user can experience the resulting feedback when a switch is activated. Especially if vision may be questionable, choose something that provides auditory or tactile feedback.'
}
const SW_EXTRACUES = {
  label: "Consider adding some extra cues to help the user activate the switch. This can help users who don't have a good sense of their position in space or how much they are moving."
}
const SW_EXTRACUES1 = {
  label: "Consider using a mirror for feedback. ",
  className: "aa-kludge-indent"
}
const SW_EXTRACUES2 = {
  label: "Provide verbal cues, such as 'feel it' to cue the user to make skin contact with the switch, then 'push it' to activate the switch and 'release it' to cue a release.",
  className: "aa-kludge-indent"
}
const SW_WAITTIME = {
  label: "Give lots of wait time, especially for new users or those who have had strokes or TBIs."
}
const SW_STRETCH = {
  label: "Sometimes users get tight after a few repetitions of activating the switch, making it hard to release. You could try giving them a little stretch to see if that helps."+
    " If it does, watch this very closely - if it doesn't resolve, you may want to avoid this movement to reduce the risk of contractures. Consult with an OT for more guidance."
}

//Speech recognition remedies
const POOR_RECOG1 = {
  label: `Follow the fundamentals of successful speech recognition (double-check!)`,
  help: `
    <div>Double-check these fundamentals:</div>
    <div><ul>
      <li>Use full phrases instead of word-by-word dictation</li>
      <li>Speak clearly, and articulate a clear beginning and end to each word in a phrase</li>
      <li>Make sure you have a decent microphone and it is positioned to pick up your voice</li>
      <li>Be sure the microphone is not touching your face</li>
      <li>Protect your voice profile</li>
    </ul>
    </div>
    <div>Here are a couple of resources for more info:
    <ul>
      <li><a target="_blank" href="https://www.callscotland.org.uk/information/speech-recognition/">Speech recognition pages provided by CALL Scotland</a>. They seem to do a good job of keeping things pretty well up to date.</li>
      <li><a target="_blank" href="https://bit.ly/srguide">Speech Recognition as AT for Writing</a> - excellent guide written by Dan Cochrane and Kelly Key</li>
    </ul>
    </div>  `
}
const BUILT_IN_OR_DRAGON = `
  <p>Sometimes one type of speech recognition will work far better than another, for the same user.
  On most devices, like laptops, tablets, and smartphones, you have some built-in (free) options. For example, Windows 11 includes voice typing, and if you use Google Docs, you also have access to voice typing within Chrome.</p>
  <p>You could also consider installing paid software such as Dragon. If you haven't had much success with the built-in options, you might give Dragon a try.</p>
  <p>Do you need to spend money purchasing speech recognition software such as Dragon?</p>
  <p>It depends on what you want / need the software to do. There are built-in / free versions available via Microsoft, Google, Apple, etc. 
  But Dragon may give better recognition for some people. We also recommend reading up on the features of the free software and comparing with paid software to understand what features are available.  
  If you want / need those additional paid features, you might try the paid software on a trial basis to see if it gives you good recognition.</p>
  <p>Note: Features can change quickly, so check periodically to see what's new in both free and paid software.</p>
  <p>See <a target="_blank" href="https://www.callscotland.org.uk/information/speech-recognition/">the speech recognition info provided by CALL Scotland</a> for more info. They seem to do a good job of keeping things pretty well up to date.</p>

`;
const POOR_RECOG2 = {
  label: `Don't stop with just one brand / application for speech. Try others.`,
  help: BUILT_IN_OR_DRAGON
}
const POOR_RECOG3 = {
  label: 'Set up efficient mouse access to make corrections and edits. This can be faster and less frustrating than using voice to make corrections.'
}
const ACCENT_RECOG = {
  label: 'Speaking English with a specific accent (such as British, or Spanish) may affect recognition accuracy. See if your recognition software has the option to change the accent, or try software that does give this option.'
}
const LANG_RECOG = {
  label: 'Note, too, that some recognition software can handle languages other than English.'
}
const MIC_VOICE1 = {
  label: `Check if your recognition systems allows you to control the microphone by voice, e.g., by saying 'Wake up'.`
}
const MIC_VOICE2 = {
  label: `Make sure you are using the correct wake command ('Voice access wake up' is different than 'wake up'!).`
}
const HEADSET = {
  label: 'If the user needs to be fully independent:'
}
const HEADSET1 = {
  label: `Consider a desk-mounted microphone or label microphone.`,
  className: 'aa-kludge-indent'
}
const HEADSET2 = {
  label: `Also consider the pros / cons of wireless headsets.`,
  className: 'aa-kludge-indent'
}
const INCONSISTENT_RECOG = {
  label: `If recognition accuracy is low, even though the user has consistent voice quality, enunciation, and volume, the problem might be the microphone.`
}
const INCONSISTENT_RECOG1 = {
  label: 'Be sure to use the same microphone with each use.',
  className: 'aa-kludge-indent'
}
const INCONSISTENT_RECOG2 = {
  label: 'Make sure the microphone is positioned in the same place each time.',
  className: 'aa-kludge-indent'
}
const INCONSISTENT_RECOG3 = {
  label: 'If using a built-in microphone, try a good headset mic.',
  className: 'aa-kludge-indent',
  help: `
  <div>
    <a target="_blank" href="https://www.callscotland.org.uk/information/speech-recognition/getting-started">CALL Scotland</a> recommends the Andrea NC 181 USB.
  </div>  `
}
const SINGLE_MIC1 = {
  label: 'You may need to use a single microphone for two functions on two separate devices, like for speech recognition on your computer AND for phone calls on your cellphone.'
}
const SINGLE_MIC2 = {
  label: `It's possible to use a single microphone / headset for two functions, but can be tricky.`
}
const SINGLE_MIC3 = {
  label: 'There are products that can be switched between the two functions by activating a button on a headset hub.',
  help: `
    <div>
    See <a target="_blank" href="https://headsetplus.com/blog/headsets-can-used-desk-phone-computer/">this article on HeadSetPlus</a> for some ideas.
    Keep in mind that technology changes quickly, so you may have to do some additional research to get the latest info.
    </div>
  `
}
const SINGLE_MIC4 = {
  label: 'Check that the user can activate that switching button successfully.'
}
const FORGET_COMMANDS1 = {
  label: `It may help to make a visual aid / list with the most important, frequently used commands. Over time you may be able to fade out visual cues and/or expand to other less frequent commands.`
}
const FORGET_PATTERN1 = {
  label: `It may help to add a visual cue.`
} 
const FORGET_PATTERN2 = {
  label: `Also consider starting with a task that uses short commands (e.g., opening programs, dictating short search terms) instead of lengthy dictation.`
}
const IGNORES_ERRORS1 = {
  label: `If the problem is *forgetting* to look for errors, it may be helpful to add a visual cue / reminder.`
}
const IGNORES_ERRORS2 = {
  label: `If the problem is *seeing well enough* to proofread for errors:`
}
const IGNORES_ERRORS2a = {
  label: `Increase contrast and/or font size`,
  className: `aa-kludge-indent`
}
const IGNORES_ERRORS2b = {
  label: `Some software has the ability to select text and read it back`,
  className: `aa-kludge-indent`
}
const IGNORES_ERRORS2c = {
  label: `Use built-in grammar / spell check tools to help identify errors`,
  className: `aa-kludge-indent`
}

const FRUSTRATION_SPEECH1 = {
  label: `Start with tasks that have a lower cognitive load. For example, just dictating into a blank document, to get the feel of how the software listens and how to speak to it. `
}
const FRUSTRATION_SPEECH2 = {
  label: `Practicing commands such as turning on and off the microphone can also decrease overall errors.`
}
const FRUSTRATION_SPEECH3 = {
  label: `Break the learning down into small chunks and focus on tasks that might give immediate success.`
}

//Eyetracking remedies
const INCON_EYETRACK1 = {
  label: 'Restart the device (a classic remedy that sometimes works!)'
}
const INCON_EYETRACK2 = {
  label: 'Make sure the eye tracker is plugged in and active.'
}
const INCON_EYETRACK3 = {
  label: 'Clean the eye tracker'
}
const INCON_EYETRACK4 = {
  label: 'Bring up the tracking window on the device. This is a feature that helps direct the setup of the eye tracker, and it gives clues for why the eye tracker is not tracking. Try to get more info from the manufacturer of your eye tracker.'
}
const INCON_EYETRACK5 = {
  label: 'Reposition the device using the tracker window for feedback and guidance.'
}
const INCON_EYETRACK6 = {
  label: 'Look at the user. Are their eyes open? Is anything occluding their eyes?'
}
const INCON_EYETRACK7 = {
  label: `If the eye tracker is positioned lower and the user needs to look down, sometimes the eye tracker interprets that as eyes closed.  
    Try moving the eye tracker up, to encourage wider eye opening naturally. 
    This also can be useful if the eye tracker performance suffers when the user accesses the lower part of the screen, but is doing fine with the upper part.
  `
}
const INCON_EYETRACK8 = {
  label: `Are you outdoors or in direct sunlight?  
  `,
  help: `
  <p>
  Sometimes this interferes with the tracker's ability to pick up the eyes.
    Some trackers do better than others outdoors. If there's a need to consistently use eye tracking outsider, research several eye trackers to find one that will work best. 
  </p>
  <div>
  See <a target="_blank" href="http://gazehelp.com/">the GazeHelp site</a> by OT Charlie Danger for some ideas, specifically the spreadsheet he's put together comparing different eye trackers.
  Charlie's notes that he has seen evidence that Irisbond outperforms most trackers outside, but personally he has only had success with the IS5TD on sunny days. The IS5TD tracker is found in the TobiiDynavox PCEye 5 and I-series devices. 
  </div>
  `
} 
const LOTS_ACTIVATIONS1 = {
  label: `Slow down the tracker's dwell time. (Careful not to set it too slow, as it then becomes much harder to hold eyes still enough and long enough to elicit a click.)`
}
const LOTS_ACTIVATIONS2 = {
  label: 'Try a non-dwell activation method, such as a switch or a blink.'
}
const LOTS_ACTIVATIONS3 = {
  label: 'Try spacing active areas on the screen further apart.'
}
const LOTS_ACTIVATIONS4 = {
  label: `Provide a 'rest' button on each screen.`
}
const LOTS_ACTIVATIONS5 = {
  label: `Expect accidental activations when learning a new screen layout. It takes time to learn a new layout well enough to anticipate where the intended target is, quickly and easily.`
}
const INACC_ACTIVATIONS1 = {
  label: `If possible, calibrate.  If calibration isn't possible for this user, you could try calibrating yourself or someone else, placed in a similar position.
   Sometimes just getting past the calibration step, even with a different person, helps get things going.
  `
}
const INACC_ACTIVATIONS2 = {
  label: `Check positioning in the eye tracker window. If the eye dots flicker, try repositioning the device.`
}
const INACC_ACTIVATIONS3 = {
  label: `Dry eyes are harder to track. Consider natural tears drops or consult eye doctor regarding chronic dry eye.`
}
const INACC_ACTIVATIONS4 = {
  label: `Observe the user's eyes as they make selections. 
  Are the eyes consistently working together to make selections, or do the eyes struggle to make coordinated movements and stay focused on the same target?
  `
}
const INACC_ACTIVATIONS4a = {
  label: `If coordinated eye control is a struggle, eyegaze access will likely be difficult.  
  Consider consulting an eyegaze expert or vision specialist. Control might improve with time and practice.
  In the meantime, be sure to consider other access approaches.
  `,
  className: `aa-kludge-indent`
}
const INACC_ACTIVATIONS5 = {
  label: `If one eye has weak muscles, it may not be able to 'reach' to certain areas of the screen. Try changing the settings to track only the strongest / neurologically intact eye and see if that helps.`
}
const INACC_ACTIVATIONS6 = {
  label: `For cognitively intact users, try to teach them to compensate, especially if the inaccuracies are related to particular parts of the screen.
  For example, when trying to select targets on the left side, the eye tracker consistently undershoots.  In that case, work on teaching the user to overshoot their targets.  
  Note: this will take some practice, and might not be successul for some users.
  `
}
const INACC_ACTIVATIONS7 = {
  label: `Some users have neurological deficits that may prevent successful use of eye tracking. 
  An example is when the user's best eye changes depending on which side of midline the target is in.
  Consider consulting an eyegaze expert or vision specialist. Control might improve with time and practice.
  In the meantime, be sure to consider other access methods.
  `
}
const NO_ACTIVATIONS4 = {
  label: `If currently dwelling to select, consider decreasing the dwell time. Sometimes that helps, but the user needs to be able to handle the quicker dwell (lots of practice may be needed).`
}
const SEE_EYEGAZE1 = {
  label: 'Increase the size and/or color contrast of targets on the screen.'
}
const SEE_EYEGAZE2 = {
  label: `Determine whether text, symbol, or both is easier to see and interpret.`
}
const SEE_EYEGAZE3 = {
  label: `Increase size of cell contents.`
}
const SEE_EYEGAZE6 = {
  label: `Note that most of these suggestions apply mainly when you have control over the targets on the screen (like using eyegaze on an AAC device).`
}
const EYEGAZE_RESOURCES = {
  label: `See these resources for other ideas`,
  help: `
  <p>Implementing eyegaze can be tricky, so it can be helpful to get guidance from trusted sources. 
  You can seek info from the eye tracker manufacturer, blog posts, other colleagues, etc.</p>
  <div>Here are a couple of resources to get you started:
    <ul>
      <li>See <a target="_blank" href="http://gazehelp.com/">the GazeHelp site</a> by OT Charlie Danger for some ideas. He's included an eye tracker decision tree and a table of eye tracker properties.</li>
      <li><a target="_blank" href= "https://www.callscotland.org.uk/information/eye-gaze/">CALL Scotland's eye gaze page</a></li>  
      <li>A variety of <a target="_blank" href="https://praacticalaac.org/?s=eye">resources about eye gaze</a> in the always-useful Praactical AAC blog</li>
      <li>Try these search terms: "eye tracker access", "eyegaze access"
    </ul>
  </div>
  <p>As always, keep in mind that technology changes quickly, so you may have to do some additional research to get the latest info.</p>  
  `
}
//Touchscreen remedies
const DIGIT_ISOLATION1 = {
  label: 'Use a stylus.',
  help: 
  `
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-3"><img class="aa-modal-image" src="/images/remedies/stylus-example-crop-1402x2439-opt.jpg" /></div>
      <div class="col-md-9">
        <p>The photo shows a basic passive stylus that will work with any touchscreen.  Inexpensive and available wherever regular tech stuff is sold
      (e.g., Best Buy).</p>
        <p>For more ideas, search the web for "stylus for accessibility" or "adaptive stylus".</p>
      </div>
    </div>
  </div>
  `
}
const DIGIT_ISOLATION2 = {
  label: 'Try a digit isolation glove.',
  help: 
  `
  <div>
    <p>The basic idea is to use a glove to reduce the amount of skin that contacts the touchscreen. Here are a few options:</p> 
    <ul>
      <li>Cut the index finger tip off of a pair of gloves</li>
      <li>Try gloves with capacitive finger tips made for touchscreen devices</li>
      <li>Search the web for "digit isolation glove"</li>
    </ul> 
  </div>
  `
}
const DIGIT_ISOLATION3 = {
  label: 'Anchor the fingers on the edge of the screen, and type with the thumb.',
  help: 
  `
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-4"><img class="aa-modal-image" src="/images/remedies/digit-isolation-remedy-1a-crop-1908x3452-opt.jpg" /></div>
      <div class="col-md-8">
        <p>Use the edge of the screen as an anchor for the fingers, as shown in the photo.</p>
        <p>This keeps the fingers out of the way, and allows typing with a more isolated thumb.</p>
        <p>Shift the hand around the screen edge to reach other areas of the screen.</p>
      </div>
    </div>
  </div>
  `

}
const DRAG_NOT_TAP = {
  label: 'Look at ways to increase stability, such as:'
}
const DRAG_NOT_TAP1 = {
  label: 'Add more support for the upper extremity.',
  className: 'aa-kludge-indent'
}
const DRAG_NOT_TAP2 = {
  label: 'Cue the user to anchor their arm before attempting to tap.',
  className: 'aa-kludge-indent'
}
const DRAG_NOT_TAP3 = {
  label: 'Try a laptray to offer more surface area to anchor or support the arm.',
  className: 'aa-kludge-indent'
}
const DRAG_NOT_TAP4 = {
  label: 'Try a digit isolating solution, to allow the user support their arm directly on the touchscreen without activating it.',
  className: 'aa-kludge-indent'
} 
const DRAG_SETTING1 = {
  label: `Check the device's accessibility features. There may be a setting for a timed hold to lock in the target and ease lift-off. (In iOS, these settings are in Touch Accommodations.)`
}
const STYLUS_SLIPS1 = {
  label: 'Build up the stylus or attach to hand',
  help: `
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-3"><img class="aa-modal-image" src="/images/remedies/built-up-stylus-example-1908x4302-opt.jpg" /></div>
      <div class="col-md-9">
        <p>Building up the stylus can make it easier to hold.  Use Coban wrap, red / blue foam, or the like to make the stylus "fatter" and softer.</p>
      </div>
    </div>
    <br />
    <div class="row">
      <div class="col-md-3"><img class="aa-modal-image" src="/images/remedies/universal-cuff-example-crop-1908x2065-opt.jpg" /></div>
      <div class="col-md-9">
        <p>You could also attach the stylus to the hand, using a universal cuff.</p>
      </div>
    </div>
  </div>
 `
}
const PICKUP_STYLUS1 = {
  label: "Change the stylus shape or how it's held.",
  help:
  `
  <div>
    <p>There's quite a bit you can do to the stylus to make it easier to manipulate. Consider these ideas:</p>
    <ul>
      <li>Be creative. Observe what is happening when the user attempts to retrieve the stylus. Make adjustments as needed.  This could be a very individualized solution.</li>
      <li>Choose a stylus with a different shape (possibly larger diameter)</li>
      <li>Add anti-slip material or build up the stylus diameter to be "fatter" and softer</li>
      <li>Try a universal cuff to attach the stylus to the user's hand (with or without a right angle pocket)</li>
    </ul> 
    <p>If you've tried all that, and it's still not working well, it may be time to reassess whether or not a stylus is an appropriate access method. Discuss with your team and/or refer to occupational therapy.</p>
  </div>
  `
}
const PICKUP_STYLUS2 = {
  label: 'Consider a docking station option.',
  help: `
  <div class="container-fluid">
    <div class="row">
      <div class="col">
        <p>A docking station provides a place to put the stylus, and holds the stylus in such a way that the user can readily pick it up again. 
        Using a docking station is especially important for mouth stylus users. </p>
        <p>You can try making your own, as in the photos below.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-md-4"><img class="aa-modal-image" src="/images/remedies/early-iteration-mouthstick-docking-station-1-crop-1908x2838-opt.jpg" /></div>
      <div class="col-md-8"><img class="aa-modal-image" src="/images/remedies/custom-stylus-docking-station-1-4032x1908-opt.jpg" /></div>
    </div>
    <br />
    <div class="row">
      <div class="col">
        <p>Do a web search on "mouthstick docking station" for more ideas and some commercially available options.</p>
      </div>
    </div>
  </div>
  `
}
const PICKUP_STYLUS3 = {
  label: 'A box or tray for the stylus might help. You could custom-make this if needed or check off-the-shelf pen trays / holders / troughs. Be sure the work surface has a space for the tray.'
}
const PICKUP_STYLUS4 = {
  label: `A piece of velcro could do the trick. Play around with the velcro surface area to adjust the strength of the 'hold'.`
}
const SEE_TOUCHSCREEN = {
  label: `Using a stylus or a cuff for typing can help with control, but it may have a side effect -- 
  the hand may block the view of the screen, making it difficult to see exactly what you're trying to tap.
  If that happens, here are a couple of things to try:  
  `
}
const SEE_TOUCHSCREEN1 = {
  label: 'Try offsetting the touchscreen.',
  className: 'aa-kludge-indent',
  help:
  `
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-3"><img class="aa-modal-image" src="/images/remedies/stylus-remedy-1-crop-1908x3281-opt.jpg" /></div>
      <div class="col-md-3"><img class="aa-modal-image" src="/images/remedies/stylus-remedy-2-crop-1908x3038-opt.jpg" /></div>
      <div class="col-md-6">
        <p>Angling the tablet may allow the user to see around their hand.</p>
        <p>You could also try angling the forearm to achieve this.</p>
        <p>The basic idea is to adjust positions so you can see the stylus tip easily.</p>
      </div>
    </div>
  </div>
  `
}
const SEE_TOUCHSCREEN2 = {
  label: 'Try a universal cuff with right angle pocket.',
  className: 'aa-kludge-indent',
  help:`
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-4"><img class="aa-modal-image" src="/images/remedies/stylus-remedy-3-crop-1908x3430-opt.jpg" /></div>
      <div class="col-md-8">
      <p>Use the right angle pocket to reposition the stylus to the side of the hand, between the thumb and index finger.
      This may give better vision of the screen, and possibly greater comfort and control in some cases.</p>
      <p>(A right angle pocket is an accessory for the universal cuff.  Search on "right angle pocket universal cuff" for more info and sources. Also called a "utensil holder." )</p>
      </div>
    </div>
  </div>
  `
}
const SEE_TOUCHSCREEN3 = {
  label: 'Try putting the stylus on the back of the hand.',
  className: 'aa-kludge-indent',
  help: 
  `
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-4"><img class="aa-modal-image" src="/images/remedies/stylus-remedy-4-crop-1908x3639-opt.jpg" /></div>
      <div class="col-md-8">
      <p>Use the universal cuff to position the stylus along the back of the hand (instead of the typical palm position).</p>
      <p>Then tap with the hand in a palm-down position, as shown in the photo.</p>
      <p>This tends to keep the hand below the level of the stylus, so it can be easier to see the stylus tip.</p>
      </div>
    </div>
  </div>
  `
}
const TOUCHSCREEN_RANGE1 = {
  label: `Change position of the device, try different angles, or offset the device toward the user's strong side.`
}
const TOUCHSCREEN_RANGE2 = {
  label: `Some devices have a 'one-handed' setting (or equivalent) that will shift targets to one side of the screen.`
}
const TOUCHSCREEN_MULTI1 = {
  label: 'Help set the user up with as much stability as possible, including: '
}
const TOUCHSCREEN_MULTI1A = {
  label: 'Appropriate positioning in a chair, wheelchair, or specialized seating system',
  className: `aa-kludge-indent`,
  help: SEATING_HELP
}
const TOUCHSCREEN_MULTI1B = {
  label: 'Try an arm support to provide more surface area to stabilize on',
  className: `aa-kludge-indent`,
}
const TOUCHSCREEN_MULTI2 = {
  label: 'Try a digit isolation glove so user can stabilize their hand directly on the screen when making a selection'
}
const TOUCHSCREEN_MULTI3 = {
  label: "Check the device's accessibility settings for options to change how the screen interprets multiple quick taps:"
}
const TOUCHSCREEN_MULTI3A = {
  label: "There may be an option to ignore repeat taps.",
  className: 'aa-kludge-indent'
}
const TOUCHSCREEN_MULTI3B = {
  label: "See if there's an option to set a required time before the touchscreen will interpret a tap.",
  className: 'aa-kludge-indent'
}
const TOUCHSCREEN_MULTI4 = {
  label: `If the inadvertent taps occur frequently and severely limit performance (e.g., user makes a mistake, then makes 10 more mistakes
    trying to correct the first mistake), consider a different access method. (For example, a switch-adapted joystick mouse instead of direct touchscreen access.)`,
  className: `aa-kludge-indent`
}


const LONG_PRESS1 = {
  label: 'Check the accessibility settings for your device. There may be options for adjusting the long press time or turning it off completely.'
}
const LONG_PRESS2 = {
  label: `There may also be an option for how long you must touch the screen before a touch is recognized.  Sometimes setting this to a value also changes the long press behavior.`
}
const LONG_PRESS3 = {
  label: `Unfortunately it is hard for us to give specific advice, since there are many platforms and they change frequently. A web search may give you instructions for your device.`
}
const SEE_AND_REACH1 = {
  label: `This is a particular type of positioning challenge: when the user can see the screen, he / she can't reach it.  And vice versa.`
}
const SEE_AND_REACH2 = {
  label: `If you're not able to find a position that allows seeing and reaching, direct touchscreen access may not be the right solution for the user, at least at this time.`
}
const SEE_AND_REACH3 = {
  label: `Assess the user for a different access method to use until they gain strength, head control, and vision to see and interact with the touchscreen at the same time.`
}
const GENERAL_TECH = {
  label: `Sometimes poor performance occurs simply because the tech equipment just isn't working properly. 
  It's hard to give very specific suggestions, but you might try the following: 
  `
}
const GENERAL_TECH1 = {
  label: `Double-check connections and setup.  Try to find documentation to help, where applicable.`,
  className: `aa-kludge-indent`
}
const GENERAL_TECH2 = {
  label: `Try to contact the manufacturer for help, or colleagues with experience.`,
  className: `aa-kludge-indent`
}
const GENERAL_TECH3 = {
  label: `Use the equipment yourself -- or have someone else try it -- to see if the same issues occur.`,
  className: `aa-kludge-indent`
}
const NOTECH_NOTICE1 = {
  label: `Be sure that the partner knows exactly what signal they are looking for from the user.`
}
const NOTECH_NOTICE2 = {
  label: `Try being alongside the partner and work together to notice the user's signals.`
}
const NOTECH_UNDERSTAND1 = {
  label: `Try demonstrating to the partner what needs to be done. This can be a model that they can follow.`
}
const NOTECH_UNDERSTAND2 = {
  label: `Ask the partner how they are approaching the task and correct any misunderstandings.`
}
const NOTECH_SPEED = {
  label: `This applies mainly to partner-assisted scanning. This is an opportunity to model for the partner. You might also try playing the role of the user, and giving very clear signals as the partner practices partner-assisted scanning with you.`
}

//ISSUE_BUTTONS arrays are used for values/labels when presenting checkboxes for user input about issues observed.
//It's also used to retrieve labels when Reviewing previous user input, and to show remedies that correspond to issues.
//NOTE: Values property must be unique (to ensure correct retrieval of data based on value property)
//NOTE: if there is a current access method category, issues for that category are shown first
//NOTE: issues with 'any' key are always shown, regardless of access method category. They display after the category-specific issues.
//TODO add other access method categories.
//TODO instead of requiring unique values, we could have a function that says that
//if a button in a specific group has the same value as a button in the general 'any' group,
//use the specific button (and not the general one)
//For remedies property, include only remedies specific to the issue, not remedies that are already in REMEDIES_OVERALL
export const ISSUE_BUTTONS_OVERALL = {
  any: [
    {
      value: 'inefficiency',
      label: 'Inefficiency',
      moreInfo: '',
      remedies: []
    },
    {
      value: 'inconsistency',
      label: 'Inconsistency',
      moreInfo: '',
      remedies: []
    },
    {
      value: 'fatigue',
      label: 'Fatigue',
      moreInfo: '',
      remedies: [ REGULAR_FATIGUE1, REGULAR_FATIGUE2, REGULAR_FATIGUE3, REDFLAG_FATIGUE ]
    },
    {
      value: 'effort',
      label: 'Excessive effort',
      moreInfo: '',
      remedies: []
    },
    {
      value: 'pain',
      label: 'Pain',
      moreInfo: '',
      remedies: [ REDFLAG_PAIN, PAIN_PATTERN1, PAIN_PATTERN2, PAIN_PATTERN3 ]
    },
    {
      value: 'posture',
      label: 'Keeping an active, functional posture',
      moreInfo: '',
      remedies: [ SEATING_GENERAL, SEATING_REFERRAL]
    }
  ],
  //TODO we don't have any Overall issues for Keyboard: are we OK with that?

  [AM_CATEGORIES.POINTING_DEVICE]: [
    {
      value: 'move-pointer',
      label: 'Moving the mouse pointer',
      moreInfo: '',
      remedies: [MOVE_POINTER]
    },
    {
      value: 'make-selection',
      label: 'Clicking (making a selection)',
      moreInfo: '',
      remedies: [MAKE_SELECTION, MAKE_SELECTION1]
    }
  ],
  [AM_CATEGORIES.SWITCH_ACCESS]: [
    {
      value: 'press-switch',
      label: 'Pressing the switch',
      moreInfo: '',
      remedies: [ SW_POSITION, SW_POSITION1, SW_POSITION2, SW_POSITION3, SW_POSITION4, SW_STABILITY, SW_STABILITY1, SW_STABILITY2, SW_ISOLATED, SW_ACTIVITY, SW_CONSISTENT, SW_FEEDBACK, SW_EXTRACUES, SW_EXTRACUES1, SW_EXTRACUES2, SW_WAITTIME]
    },
    {
      value: 'release-switch',
      label: 'Releasing the switch',
      moreInfo: '',
      remedies: [ SW_POSITION, SW_POSITION1, SW_POSITION2, SW_POSITION3, SW_POSITION4, SW_STRETCH, SW_EXTRACUES, SW_EXTRACUES1, SW_EXTRACUES2, SW_WAITTIME ]
    }
  ],
  [AM_CATEGORIES.SPEECH_RECOGNITION]: [
    {
      value: 'recognition-accuracy',
      label: 'Poor recognition accuracy',
      moreInfo: '',
      remedies: [ POOR_RECOG1, POOR_RECOG2, POOR_RECOG3 ]
    },
  ],
  [AM_CATEGORIES.EYEGAZE_ACCESS]: [
    {
      value: 'lots-activations',
      label: 'Lots of accidental selections',
      moreInfo: '',
      remedies: [ LOTS_ACTIVATIONS1, LOTS_ACTIVATIONS2, LOTS_ACTIVATIONS3, LOTS_ACTIVATIONS4, LOTS_ACTIVATIONS5, EYEGAZE_RESOURCES ]
    },
    {
      value: 'inaccurate-activations',
      label: 'Eye tracker is not very accurate when making selections',
      moreInfo: '',
      remedies: [ INACC_ACTIVATIONS1, INACC_ACTIVATIONS2, INACC_ACTIVATIONS3, INACC_ACTIVATIONS4, INACC_ACTIVATIONS4a, INACC_ACTIVATIONS5, INACC_ACTIVATIONS6, INACC_ACTIVATIONS7, EYEGAZE_RESOURCES ]
    },

  ]

};
export const ISSUE_BUTTONS_MOTORIC = {
  //TODO when there is a category, do we also want to include these general issues?  Note that they have no remedies at this point.
  any: [
    {
      value: 'range-of-motion',
      label: 'Insufficient range of motion',
      moreInfo: '',
      remedies: []
    },
    {
      value: 'imprecision',
      label: 'Imprecise movements',
      moreInfo: '',
      remedies: []
    },
    {
      value: 'extraneous',
      label: 'Extraneous movements',
      moreInfo: '',
      remedies: []
    },
  ],
  [AM_CATEGORIES.KEYBOARD]: [
    {
      value: 'key-isolation',
      label: 'Hitting multiple keys at once, by mistake',
      moreInfo: '',
      remedies: [ KEY_ISOLATE, KEY_ISOLATE1, KEY_ISOLATE2, KEY_ISOLATE3, KEY_ISOLATE4 ]
    },
    {
      value: 'key-modifiers',
      label: 'Difficulty holding down two keys at once, for combos like CTRL + C',
      moreInfo: '',
      remedies: [ KEY_MODIFIERS ]
    },
    {
      value: 'key-repeats',
      label: 'Difficulty releasing keys quickly enough to avoid letter repeats',
      moreInfo: '',
      remedies: [ KEY_REPEAT ]
    },
    {
      value: 'reach-all-keys',
      label: 'Difficulty reaching all areas of the keyboard',
      moreInfo: '',
      remedies: [ KEY_RANGE1, KEY_RANGE2 ]
    }

  ],
  [AM_CATEGORIES.POINTING_DEVICE]: [
    {
      value: 'pointer-range',
      label: 'Difficulty reaching all parts of the screen',
      moreInfo: '',
      remedies: [ POINTER_RANGE1, POINTER_RANGE2, POINTER_RANGE3 ]
    },
    {
      value: 'overshoot',
      label: 'Overshooting target with lots of corrective movements',
      moreInfo: '',
      remedies: [ OVERSHOOT1, OVERSHOOT2, OVERSHOOT3, OVERSHOOT4 ]
    },
    {
      value: 'move-during-click',
      label: 'Missed target because pointer moves when clicking',
      moreInfo: '',
      remedies: [ CLICK_MOVE1, CLICK_MOVE2, CLICK_MOVE3, CLICK_MOVE4, CLICK_MOVE5 ]
    },
    {
      value: 'move-during-lift',
      label: 'Difficulty lifting off the pointing device without moving the pointer',
      moreInfo: '',
      remedies: [ LIFT_MOVE1, LIFT_MOVE2, LIFT_MOVE3, LIFT_MOVE4]
    },
    {
      value: 'difficult-double-click',
      label: 'Difficulty with double clicking',
      moreInfo: '',
      remedies: [ DOUBLE_CLICK1, DOUBLE_CLICK2, DOUBLE_CLICK3 ]
    },
    {
      value: 'difficult-right-click',
      label: 'Difficulty with right clicking',
      moreInfo: '',
      remedies: [ RIGHT_CLICK1, RIGHT_CLICK2, RIGHT_CLICK2_1, RIGHT_CLICK2_2, RIGHT_CLICK3, RIGHT_CLICK4 ]
    },
  ],
  [AM_CATEGORIES.SWITCH_ACCESS]: [
    {
      value: 'high-force',
      label: 'Too much force',
      moreInfo: '',
      remedies: [ SW_STRETCH, SW_FEEDBACK ]
    },
    {
      value: 'low-force',
      label: 'Too little force',
      moreInfo: '',
      remedies: [ SW_FEEDBACK, SW_EXTRACUES, SW_EXTRACUES1, SW_EXTRACUES2 ]
    }
  ],
  [AM_CATEGORIES.SPEECH_RECOGNITION]: [
    {
      value: 'activate-mic',
      label: `Difficulty activating microphone button`,
      moreInfo: '',
      remedies: [ MIC_VOICE1, MIC_VOICE2 ]
    },
    {
      value: 'manage-headset',
      label: `Difficulty putting on / taking off headset independently`,
      moreInfo: '',
      remedies: [ HEADSET, HEADSET1, HEADSET2 ]
    },
    {
      value: 'accented-speech',
      label: `Poor recognition accuracy due to user's accent`,
      moreInfo: '',
      remedies: [ ACCENT_RECOG, LANG_RECOG ]
    },
  ],
  [AM_CATEGORIES.EYEGAZE_ACCESS]: [
    {
      value: 'oculomotor',
      label: 'Uncoordinated eye movements',
      moreInfo: '',
      remedies: [ INACC_ACTIVATIONS4a ]
    },
  ],
  [AM_CATEGORIES.TOUCHSCREEN]: [
    {
      value: 'digit-isolation',
      label: `Difficulty isolating one digit`,
      moreInfo: '',
      remedies: [ DIGIT_ISOLATION1, DIGIT_ISOLATION2, DIGIT_ISOLATION3 ]
    }, 
    {
      value: 'stylus-slips',
      label: `Stylus slips out of hand when trying to tap`,
      moreInfo: '',
      remedies: [ STYLUS_SLIPS1 ]
    },   
    {
      value: 'pick-up-stylus',
      label: `Can't pick up stylus after putting it down`,
      moreInfo: '',
      remedies: [ PICKUP_STYLUS1, PICKUP_STYLUS2, PICKUP_STYLUS3, PICKUP_STYLUS4 ]
    },  
    {
      value: `touchscreen-reach`,
      label: `Trouble reaching all areas of the screen`,
      remedies: [ TOUCHSCREEN_RANGE1, TOUCHSCREEN_RANGE2 ]
    },
    {
      value: 'drag-not-tap',
      label: `Touchscreen interprets a drag instead of intended tap`,
      moreInfo: '',
      remedies: [ DRAG_NOT_TAP, DRAG_NOT_TAP1, DRAG_NOT_TAP2, DRAG_NOT_TAP3, DRAG_NOT_TAP4, DRAG_SETTING1 ]
    },    
    {
      value: 'touchscreen-multi',
      label: `Lots of inadvertent taps`,
      remedies: [ TOUCHSCREEN_MULTI1, TOUCHSCREEN_MULTI1A, TOUCHSCREEN_MULTI1B, TOUCHSCREEN_MULTI2, TOUCHSCREEN_MULTI3, TOUCHSCREEN_MULTI3A, TOUCHSCREEN_MULTI3B, TOUCHSCREEN_MULTI4 ]
    },    
    {
      value: 'touchscreen-longtap',
      label: `Touchscreen interprets a long press instead of intended regular tap`,
      remedies: [ LONG_PRESS1, LONG_PRESS2, LONG_PRESS3 ]
    } 
  ]
}

export const ISSUE_BUTTONS_COGPERC = {
  any: [
    {
      value: 'vision',
      label: 'Seeing well enough',
      moreInfo: '',
      remedies: [ VISION_REVIEW, VISION_LOS ]
    },
    {
      value: 'understanding',
      label: 'Understanding the tasks',
      moreInfo: '',
      remedies: [ UNDERSTANDING_GENERAL1, UNDERSTANDING_GENERAL2, UNDERSTANDING_GENERAL3 ]
    },
    {
      value: 'attention',
      label: 'Maintaining attention during the tasks',
      moreInfo: '',
      remedies: [ ATTENTION_ENV, ATTENTION_ACTIVITY, ATTENTION_SPLIT, ATTENTION_NOTECH]
    },
    {
      value: 'motivation',
      label: 'Motivation to use the access method',
      moreInfo: '',
      remedies: [ MOTIVATION_ACTIVITY, MOTIVATION_FEEDBACK ]
    },
  ],
  [AM_CATEGORIES.KEYBOARD]: [
    {
      value: 'seeing-keys',
      label: 'Difficulty seeing the keys for hunt / peck typing',
      moreInfo: '',
      remedies: [ SEE_KEYS1 ]
    },
  ],
  [AM_CATEGORIES.POINTING_DEVICE]: [
    {
      value: 'see-pointer',
      label: 'Locating the mouse pointer on the screen',
      moreInfo: '',
      remedies: [ LOCATE_POINTER1, LOCATE_POINTER2, LOCATE_POINTER3]
    },
    {
      value: 'track-pointer',
      label: 'Tracking the mouse pointer as it moves',
      moreInfo: '',
      remedies: [ TRACK_POINTER1, TRACK_POINTER2 ]
    },
  ],
  [AM_CATEGORIES.SWITCH_ACCESS]: [
    {
      value: 'see-switch',
      label: 'Knowing the switch location',
      moreInfo: '',
      remedies: [ SW_VISION, SW_ACTIVITY, SW_CONSISTENT ]
    },
  ],
  [AM_CATEGORIES.EYEGAZE_ACCESS]: [
    {
      value: 'see-eyegaze',
      label: 'Trouble seeing eyegaze targets clearly',
      moreInfo: '',
      remedies: [ SEE_EYEGAZE1, SEE_EYEGAZE2, SEE_EYEGAZE3, SEE_EYEGAZE6 ]
    },
  ],
  [AM_CATEGORIES.SPEECH_RECOGNITION]: [
    {
      value: 'forget-speech-commands',
      label: 'Trouble remembering the speech commands',
      moreInfo: '',
      remedies: [ FORGET_COMMANDS1 ]
    },
    {
      value: 'forget-speech-pattern',
      label: `Forgetting to speak the 'right' way`,
      moreInfo: '',
      remedies: [ FORGET_PATTERN1, FORGET_PATTERN2 ]
    },
    {
      value: 'ignores-errors',
      label: `Not noticing recognition errors`,
      moreInfo: '',
      remedies: [ IGNORES_ERRORS1, IGNORES_ERRORS2, IGNORES_ERRORS2a, IGNORES_ERRORS2b, IGNORES_ERRORS2c ]
    },
    {
      value: 'frustration-speech',
      label: 'High frustration using speech recognition',
      moreInfo: '',
      remedies: [ FRUSTRATION_SPEECH1, FRUSTRATION_SPEECH2, FRUSTRATION_SPEECH3 ]
    },
  ],
  [AM_CATEGORIES.TOUCHSCREEN]: [
    {
      value: 'see-touchscreen',
      label: 'View of screen blocked by hand / stylus',
      moreInfo: '',
      remedies: [ SEE_TOUCHSCREEN, SEE_TOUCHSCREEN1, SEE_TOUCHSCREEN2, SEE_TOUCHSCREEN3 ]
    },
    {
      value: `see-and-reach`,
      label: `Can't find a position that allows both seeing and reaching`,
      remedies: [ SEE_AND_REACH1, SEE_AND_REACH2, SEE_AND_REACH3 ]
    },
  ],
};

export const ISSUE_BUTTONS_TECH = {
  any: [
    {
      value: 'tech-problems',
      label: `General technical problem with the equipment`,
      moreInfo: '',
      remedies: [ GENERAL_TECH, GENERAL_TECH1, GENERAL_TECH2, GENERAL_TECH3 ]
    },
  ],
  [AM_CATEGORIES.SPEECH_RECOGNITION]: [
    {
      value: 'inconsistent-recog',
      label: `Inconsistent accuracy despite consistent speech`,
      moreInfo: '',
      remedies: [ INCONSISTENT_RECOG, INCONSISTENT_RECOG1, INCONSISTENT_RECOG2, INCONSISTENT_RECOG3  ]
    },
    {
      value: 'two-mic-functions',
      label: `Need to use one microphone on two different devices`,
      moreInfo: '',
      remedies: [ SINGLE_MIC1, SINGLE_MIC2, SINGLE_MIC3, SINGLE_MIC4 ]
    }
  ],
  [AM_CATEGORIES.EYEGAZE_ACCESS]: [
    {
      value: 'inconsistent-eyetracker',
      label: `Eye tracker not consistently picking up the eyes`,
      moreInfo: '',
      remedies: [ INCON_EYETRACK1,  INCON_EYETRACK2, INCON_EYETRACK3, INCON_EYETRACK4, INCON_EYETRACK5, INCON_EYETRACK6, INCON_EYETRACK7, INCON_EYETRACK8, EYEGAZE_RESOURCES ]
    },
    {
      value: 'no-activations',
      label: 'Trouble selecting any item with eyegaze',
      moreInfo: '',
      remedies: [ INACC_ACTIVATIONS1, INACC_ACTIVATIONS2, INACC_ACTIVATIONS3, NO_ACTIVATIONS4, LOTS_ACTIVATIONS2, EYEGAZE_RESOURCES ]
    },
  ]
};
export const ISSUE_BUTTONS_NOTECH = {
  any: [
    {
      value: 'notice-action',
      label: `Partner didn't notice action reliably`,
      moreInfo: '',
      remedies: [ NOTECH_NOTICE1, NOTECH_NOTICE2 ]
    },
    {
      value: 'partner-understand',
      label: `Partner didn't understand the task`,
      moreInfo: '',
      remedies: [ NOTECH_UNDERSTAND1, NOTECH_UNDERSTAND2 ]
    },
    {
      value: 'partner-pace',
      label: `Partner went too fast (or too slow)`,
      moreInfo: '',
      remedies: [ NOTECH_SPEED ]
    }
  ]
};

//return an array of all the issue buttons, across all categories
export function extractIssueButtons(){
  let issueBtns = [];
  let overallBtns = Object.values(ISSUE_BUTTONS_OVERALL).flat();
  let motorBtns = Object.values(ISSUE_BUTTONS_MOTORIC).flat();
  let cogpercBtns = Object.values(ISSUE_BUTTONS_COGPERC).flat();
  let techBtns = Object.values(ISSUE_BUTTONS_TECH).flat();
  let noTechBtns = Object.values(ISSUE_BUTTONS_NOTECH).flat();
  issueBtns = overallBtns.concat(motorBtns, cogpercBtns, techBtns, noTechBtns);
  return issueBtns;
}

export function getIssueLabelForValue(value){
  let issueBtns = extractIssueButtons();
  let matchingLabel = "";
  issueBtns.forEach(issue => {
    if(issue.value === value)
      matchingLabel = issue.label;
  })
  return matchingLabel;
}
//Returns the remedies for a given issue string.
//If there are none, it returns an empty array.
export function getRemediesForValue(value){
  let issueBtns = extractIssueButtons();
  let matchingRemedies = [];
  issueBtns.forEach(issue => {
    if(issue.value === value){
      if(typeof issue.remedies !== 'undefined')
        matchingRemedies = issue.remedies;
    }
  });
  return matchingRemedies;
}